import * as React from "react";
import { useTranslation } from 'react-i18next';
import { Routes, Route, useParams, useSearchParams } from "react-router-dom";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import Paper from "@mui/material/Paper";
import DeleteIcon from "@mui/icons-material/Delete";
import { useDialogs } from "@toolpad/core/useDialogs";
import SecurityIcon from "@mui/icons-material/Security";
import EditIcon from "@mui/icons-material/Edit";
import ShareIcon from "@mui/icons-material/Share";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import VisibilityIcon from '@mui/icons-material/Visibility';
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import AppContext from "../../utils/context";
import Box from "@mui/material/Box";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Select from "@mui/material/Select";
import { formatCell } from "../../components/autocell.js";
import { callRpc } from '../../utils/rpc';

export default function View() {
  const dialogs = useDialogs();
  const { t } = useTranslation();
  const appContext = React.useContext(AppContext);
  const [project, setProject] = React.useState(null);
  const [columns, setColumns] = React.useState([]);
  const [rows, setRows] = React.useState([]);
  const [views, setViews] = React.useState([]);
  const [currentView, setCurrentView] = React.useState("");
  const [anchorElProject, setAnchorElProject] = React.useState(null);
  const { events } = appContext;
  const [filters, setFilters] = React.useState({ items: [] });
  const [instanceId, setInstanceId] = React.useState(null);
  const [selectedRowId, setSelectedRowId] = React.useState(null);
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(50); // default page size
  const dataGridRef = React.useRef();

  let { projectId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  React.useEffect(() => {
    const off = events.on("refresh-instances", () => loadProject());
    return () => {
      off();
    };
  }, []);

  React.useEffect(() => {
    loadProject();
  }, [projectId, instanceId]);

  React.useEffect(() => {
    if (project) {
      if (searchParams.get("instance_id")) {
        setInstanceId(searchParams.get("instance_id"));
        let act = (searchParams.get("action") || "services").toLowerCase();
        const tmpIntstance = { id: searchParams.get("instance_id") };
        searchParams.delete("instance_id");
        if (searchParams.get("action")) searchParams.delete("action");
        setSearchParams(searchParams);

        if (act == "services") {
          events.dispatch("list-services", {
            write: canWrite(),
            instance: {
              ...tmpIntstance,
              projects: { workspace_id: project.workspace_id },
            },
            callback: () => {
              // loadProject();
            },
          });
        }
        else if (act == "edit") {
          events.dispatch("edit-instance", {
            write: canWrite(),
            instance: tmpIntstance,
            workspaceId: project.workspace_id,
            project: project,
            callback: () => {
              loadProject();
            },
          });
        }
        else if (act == "secrets") {
          events.dispatch("list-secrets", {
            write: canWrite(),
            data: tmpIntstance,
            callback: () => { },
          });
        }
      }
    }
  }, [project]);

  React.useEffect(() => {
    if (instanceId) {
      setSelectedRowId(instanceId);
      
    }
  }, [instanceId, rows, pageSize]);

  const isProjectManager = () => {
    if (!project) return false;
    const workspace = appContext.user?.workspaces?.find((w) => w.id == project.workspace_id);
    if (!workspace) return false;
    return workspace.is_manager;
  };

  const canWrite = () => {
    if (!project) return false;
    const wk = appContext.user?.workspaces?.find((w) => w.id == project.workspace_id);
    if (!wk) return false;
    let pr = wk.projects.find((p) => p.id == project.id);
    if (!pr) return false;
    return pr.write;
  }

  // AB : get user roles
  const userRoles = appContext.user?.role ?? [];
  const isSystem = userRoles.includes('system') || userRoles.includes('owner_system');

  const settings = [
    {
      name: t('projects.settings.edit'),
      visible: project && isProjectManager(),
      onclick: () => {
        events.dispatch("edit-project", {
          data: project,
          callback: () => {
            events.dispatch("refresh-projects");
            loadProject();
          },
        });
        handleCloseProjectMenu();
      },
    },
    {
      name: t('projects.settings.delete.label'),
      visible: project && isProjectManager(),
      onclick: async () => {
        const confirmed = await dialogs.confirm(
          t('projects.settings.delete.confirm', { name: project.name }),
          {
            okText: t('commons.yes'),
            cancelText: t('commons.no'),
          }
        );
        if (confirmed) {
          const { data, error } = await appContext.database
            .from("projects")
            .delete()
            .eq("id", projectId)
            .select();

          if (!error) {
            appContext.router.navigate(`/`);
            events.dispatch("refresh-projects");
            events.dispatch("alert-message", {
              data: {
                message: t('projects.settings.delete.success', { name: project.name }),
                severity: "success",
              },
            });
          } else {
            events.dispatch("alert-message", {
              data: {
                message: t('projects.settings.delete.error', { name: project.name }),
                severity: "error",
              },
            });
          }
        }
        handleCloseProjectMenu();
      },
    },
    {
      name: t('buttons.logs'),
      visible: true,
      onclick: () => {
        appContext.router.navigate(`/logs?project_id=${projectId}`);
        handleCloseProjectMenu();
      },
    },
  ];

  const loadProject = async () => {

    const { data, error } = await appContext.database
      .from("projects")
      .select(
        `
        id,
        workspace_id,
        project_template_id,
        metadata->>default_view,
        metadata->>name,
        metadata->all_views,
        metadata,
        workspaces(name)
      `
      )
      .eq("id", projectId)
      .single();
    setProject(data);
    // Force setCurrentView even if it's the same value to retrigger useEffect
    setCurrentView(undefined);
    setTimeout(() => setCurrentView(data?.default_view), 0);
  };

  const loadView = async (view, params) => {
    const { data: viewData, error: errorData } = await callRpc(
      appContext.database,
      view, params);

    setRows(viewData.rows);
    setColumns(viewData.columns);
    if (viewData?.views) {
      setViews(viewData.views);
    }
  };


  React.useEffect(() => {
    if (project && currentView) {
      const load = async () => {
        await loadView(currentView, {
          project_id: project.id
        });
      };
      load();
    }
  }, [currentView]);


  const editServicesInstance = async (instance) => {
    events.dispatch("list-services", {
      write: canWrite(),
      instance: {
        ...instance,
        projects: { workspace_id: project.workspace_id },
      },
      callback: () => {
        // loadProject();
      },
    });
  };

  const editSecretsInstance = async (instance) => {
    events.dispatch("list-secrets", {
      write: canWrite(),
      data: instance,
      callback: () => {
        // loadProject();
      },
    });
  };

  const staticColumns = [
    {
      field: "actions",
      type: "actions",
      width: 50,
      getActions: (params) => {
        const servicesAction = <GridActionsCellItem
          icon={<ShareIcon />}
          label={t('commons.services')}
          onClick={() => editServicesInstance(params.row)}
          showInMenu
        />;
        const editAction = <GridActionsCellItem
          icon={<EditIcon />}
          label={t('buttons.edit')}
          onClick={() => {
            if (canWrite() == false) return;
            events.dispatch("edit-instance", {
              write: canWrite(),
              instance: params.row,
              workspaceId: project.workspace_id,
              project: project,
              callback: () => {
                loadProject();
              },
            });
          }}
          showInMenu
        />;

        const secretsAction = <GridActionsCellItem
          icon={<SecurityIcon />}
          label={t('buttons.secrets')}
          onClick={() => editSecretsInstance(params.row)}
          showInMenu
        />;

        const logsAction = <GridActionsCellItem
          icon={<FormatListBulletedIcon />}
          label={t('buttons.logs')}
          onClick={() => {
            appContext.router.navigate(`/logs?instance_id=${params.row.id}`);
            return; //AB hide logs
          }}
          showInMenu
        />

        // Bloc AB : edit and logs only if system
       const actions = [servicesAction];
        if (isSystem) actions.push(editAction);
        if (isSystem) actions.push(secretsAction);
        if (isSystem) actions.push(logsAction);

        return actions;
        //

        // return [servicesAction, editAction, secretsAction, deleteAction, logsAction];
        //return canWrite() ? [servicesAction, editAction, secretsAction, logsAction] : [servicesAction, secretsAction, logsAction]; // AB hide logs
      },
    },
  ];

  const handleOpenProjectMenu = (event) => {
    setAnchorElProject(event.currentTarget);
  };

  const handleCloseProjectMenu = () => {
    setAnchorElProject(null);
  };

  return (
    <div style={{ flexGrow: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
      <div className="wrapper" style={{ display: "flex", padding: "20px", minWidth: 0 }}>
        <Paper
          style={{ flexGrow: 1, display: "flex", flexDirection: "column", minWidth: 0 }}
        >
          <Box sx={{ padding: "10px", display: "flex", flexDirection: "row" }}>
            <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center" }}>
              {project && (
                <>
                  <Breadcrumbs aria-label="breadcrumb">
                    <Typography sx={{ color: 'text.primary' }}>{project.workspaces.name}</Typography>
                    <Typography sx={{ color: 'text.primary' }}>{project?.name}</Typography>
                  </Breadcrumbs>
                  {canWrite() && <EditIcon style={{ marginLeft: 10 }} />}
                  {project.write == false && <VisibilityIcon style={{ marginLeft: 10 }} />}
                </>
              )}
            </Box>
            <Box>
              {
                project && project?.default_view && views && Array.isArray(views) && (
                  <Box sx={{ mr: 1, display: "inline-block" }}>
                    <Select
                      value={currentView || ""}
                      onChange={async (e) => {
                        setCurrentView(undefined);
                        setTimeout(() => setCurrentView(e.target.value), 0);
                      }}
                      size="small"
                      displayEmpty
                    >
                      <MenuItem key={project.default_view} value={project.default_view}>
                        Default view
                      </MenuItem>
                      {views.map((v) => (
                        <MenuItem key={v.view} value={v.view}>
                          {v.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                )
              }
              {project && canWrite() && <Button
                startIcon={<AddCircleOutlineIcon />}
                onClick={() => {
                  events.dispatch("create-instance", {
                    project: project,
                    callback: (result) => {
                      loadProject();
                    },
                  });
                }}
              >
                {t('buttons.add')}
              </Button>}
              <IconButton onClick={handleOpenProjectMenu}>
                <SettingsSuggestIcon />
              </IconButton>
              <Menu
                sx={{ mt: "45px" }}
                id="menu-appbar"
                anchorEl={anchorElProject}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                keepMounted
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                open={Boolean(anchorElProject)}
                onClose={handleCloseProjectMenu}
              >
                {settings.map((setting, index) =>
                  setting?.visible ? (
                    <MenuItem key={index} onClick={() => setting.onclick()}>
                      <Typography sx={{ textAlign: "center" }}>
                        {setting.name}
                      </Typography>
                    </MenuItem>
                  ) : null
                )}
              </Menu>
            </Box>
          </Box>
          {project && (
            <DataGrid
              ref={dataGridRef}
              rows={rows}
              columns={[
                ...formatCell(columns),
                ...staticColumns,
              ]}
              page={page}
              onPageChange={setPage}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              rowSelectionModel={selectedRowId ? [selectedRowId] : []}
              onRowSelectionModelChange={(ids) => setSelectedRowId(ids[0] || null)}
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: pageSize,
                  },
                },
                sorting: {
                  sortModel: [{ field: "name", sort: "asc" }],
                },
              }}
              disableMultipleRowSelection={true}
              disableColumnResize={true}
              filterModel={filters}
              onFilterModelChange={(model) => setFilters(model)}
              sx={{ border: "none", flexGrow: 1, minWidth: 0 }}
              onRowDoubleClick={(params) => {
                editServicesInstance(params.row);
              }}
              disableSelectionOnClick
            />
          )}
        </Paper>
      </div>
    </div>
  );
}
